import { useContext, useMemo } from 'react';
import SingleUseMetricPopupContext from '../../../../../contexts/SingleUseMetricPopupContext';
import InSituMetricEditorContext from '../../../../../contexts/InSituMetricEditorContext';
import { BonusMetricLockContext } from '../MetricForm/BonusMetricLockProvider';

export type MetricAction = {
  label: string;
  onClick: () => void;
  isDisabled?: boolean;
  disabledReason?: string;
};

const useActions = ({
  onSave,
  editingState,
  isEditing,
  usageCount,
}: {
  onSave: ({
    statusOverride,
    isSaveAs,
  }: {
    statusOverride?: Metrics.MetricStatus;
    isSaveAs?: boolean;
  }) => void;
  editingState: EditingState;
  isEditing: boolean;
  usageCount: number;
}) => {
  const { isSingleUsePopup, isSaveToDataManager } = useContext(
    SingleUseMetricPopupContext,
  );
  const { isInSituEditor } = useContext(InSituMetricEditorContext);
  const {
    usages: bonusUsages,
    isLoading: isBonusUsageLoading,
    isLocked: isBonusLocked,
    disabledReason: bonusDisabledReason,
  } = useContext(BonusMetricLockContext);
  // Changes to an existing metric are blocked while bonus program usage is
  // unknown, or when it is in a bonus program and the user is not staff
  const isBonusBlocked = isBonusUsageLoading || isBonusLocked;
  const isInBonus = bonusUsages.length > 0;

  const basePrimaryAction = useMemo<MetricAction>(() => {
    if (isSingleUsePopup) {
      const statusOverride = isSaveToDataManager ? 'public' : 'single use';
      switch (editingState) {
        case 'new metric':
          return {
            label: 'Add To Gadget',
            onClick: () => onSave({ statusOverride }),
          };
        case 'no changes':
          return {
            label: 'Update',
            onClick: window.tokenFunction,
            isDisabled: true,
          };
        case 'has changes':
          return {
            label: 'Update',
            onClick: () => onSave({ statusOverride }),
          };
        case 'invalid form':
        default:
          return {
            label: 'Add To Gadget',
            onClick: window.tokenFunction,
            isDisabled: true,
          };
      }
    } else if (isInSituEditor) {
      switch (editingState) {
        case 'new metric':
          return {
            label: 'Add To Gadget',
            onClick: () => {
              const e = new Error();
              e.name = 'Unexpected state: new metric';
              throw e;
            },
          };
        case 'no changes':
        case 'no changes - archived':
          return {
            label: 'Update',
            onClick: window.tokenFunction,
            isDisabled: true,
          };
        case 'has changes':
        case 'has changes - archived':
          return {
            label: 'Update',
            onClick: () => onSave({}),
          };
        case 'invalid form':
          return {
            label: isEditing ? 'Save' : 'Create Metric',
            onClick: window.tokenFunction,
            isDisabled: true,
          };
      }
    }

    switch (editingState) {
      case 'new metric':
        return {
          label: 'Create Metric',
          onClick: () => onSave({}),
        };
      case 'no changes':
        return {
          label: 'Save Changes',
          onClick: window.tokenFunction,
          isDisabled: true,
        };
      case 'no changes - archived':
        return {
          label: 'Unarchive',
          onClick: () => onSave({ statusOverride: 'public' }),
        };
      case 'has changes':
        return {
          label: 'Save Changes',
          onClick: () => onSave({}),
        };

      case 'has changes - archived':
        return {
          label: 'Save & Unarchive',
          onClick: () => onSave({ statusOverride: 'public' }),
        };
      case 'invalid form':
      default:
        return {
          label: isEditing ? 'Save' : 'Create Metric',
          onClick: window.tokenFunction,
          isDisabled: true,
        };
    }
  }, [
    editingState,
    isEditing,
    isInSituEditor,
    isSaveToDataManager,
    isSingleUsePopup,
    onSave,
  ]);

  const primaryAction = useMemo<MetricAction>(() => {
    if (!isBonusBlocked || editingState === 'new metric') {
      return basePrimaryAction;
    }

    return {
      ...basePrimaryAction,
      onClick: window.tokenFunction,
      isDisabled: true,
      disabledReason: bonusDisabledReason,
    };
  }, [basePrimaryAction, bonusDisabledReason, editingState, isBonusBlocked]);

  const baseSecondaryActions = useMemo<MetricAction[] | undefined>(() => {
    if (isSingleUsePopup || isInSituEditor) {
      return undefined;
    }
    switch (editingState) {
      case 'new metric':
        return undefined;
      case 'no changes':
      case 'has changes':
        return [
          {
            label: 'Save As',
            onClick: () => onSave({ statusOverride: 'public', isSaveAs: true }),
          },
          ...(usageCount === 0 && !isInBonus
            ? [
                {
                  label: 'Archive',
                  onClick: () => onSave({ statusOverride: 'archived' }),
                },
              ]
            : []),
        ];
      case 'no changes - archived':
        return [
          {
            label: 'Save As',
            onClick: () => onSave({ statusOverride: 'public', isSaveAs: true }),
          },
        ];
      case 'has changes - archived':
        return [
          {
            label: 'Save Changes',
            onClick: () => onSave({}),
          },
          {
            label: 'Save As',
            onClick: () => onSave({ statusOverride: 'public', isSaveAs: true }),
          },
        ];
      case 'invalid form':
      default:
        return undefined;
    }
  }, [
    editingState,
    isInBonus,
    isInSituEditor,
    isSingleUsePopup,
    onSave,
    usageCount,
  ]);

  // Save As creates a separate copy, leaving the existing metric untouched
  const secondaryActions = useMemo<MetricAction[] | undefined>(() => {
    if (!baseSecondaryActions || !isBonusBlocked) {
      return baseSecondaryActions;
    }

    return baseSecondaryActions.filter((a) => a.label === 'Save As');
  }, [baseSecondaryActions, isBonusBlocked]);

  return {
    primaryAction,
    secondaryActions,
  };
};

export default useActions;
